import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Loader2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { ResultContext } from "./ResultSetup";

interface Props {
  context: ResultContext;
}

const normalizeCode = (s: string) => s.replace(/\s+/g, "").toUpperCase();

function deriveRemark(grade: string | null): string | null {
  if (!grade) return null;
  const g = grade.toUpperCase().replace(/[+-]/g, "");
  if (["A", "B", "C"].includes(g)) return "PASS";
  if (g === "D") return "SUPP";
  if (g === "F") return "FAIL";
  return null;
}

export function IndividualEntry({ context }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedStudentKey, setSelectedStudentKey] = useState("");
  const [moduleGrades, setModuleGrades] = useState<Record<string, { grade: string; marks: string }>>({});
  const [overallRemark, setOverallRemark] = useState("");
  const [gpa, setGpa] = useState("");

  const isReady = context.semester && context.selectedClass && context.examType && context.academicYear;
  const isPre = context.studentSource === "pre_registered";

  // Signed-up students
  const { data: signedUpStudents = [] } = useQuery({
    queryKey: ["ind-students", context.selectedClass],
    queryFn: async () => {
      if (!context.selectedClass) return [];
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, form_iv_index")
        .eq("class", context.selectedClass)
        .eq("status", "active")
        .order("first_name");
      return data ?? [];
    },
    enabled: !!context.selectedClass && !isPre,
  });

  // Pre-registered students
  const { data: preRegStudents = [] } = useQuery({
    queryKey: ["ind-prereg-students", context.selectedClass, context.semester],
    queryFn: async () => {
      if (!context.selectedClass) return [];
      const { data } = await (supabase as any)
        .from("student_pre_registrations_exam_safe")
        .select("id, full_name, form_iv_index, course, semester, class")
        .eq("class", context.selectedClass)
        .eq("semester", context.semester)
        .order("full_name");
      return data ?? [];
    },
    enabled: !!context.selectedClass && isPre,
  });

  const studentOptions = isPre
    ? preRegStudents.map((s) => ({
        key: s.id,
        label: `${s.full_name || "—"} (${s.form_iv_index})`,
        preRegId: s.id,
        formIvIndex: s.form_iv_index,
        studentId: null as string | null,
      }))
    : signedUpStudents.map((s) => ({
        key: s.id,
        label: `${s.first_name} ${s.last_name} (${s.student_number})`,
        preRegId: null as string | null,
        formIvIndex: s.form_iv_index,
        studentId: s.id,
      }));

  // Modules for the class/semester
  const { data: modules = [] } = useQuery({
    queryKey: ["ind-modules", context.selectedClass, context.semester],
    queryFn: async () => {
      if (!context.selectedClass || !context.semester) return [];
      const { data } = await supabase
        .from("modules")
        .select("id, module_code, module_name")
        .eq("class", context.selectedClass)
        .eq("semester", context.semester)
        .order("module_code");
      return data ?? [];
    },
    enabled: !!context.selectedClass && !!context.semester,
  });

  // Course for the class (needed for course_id in exam_results)
  const { data: courses = [] } = useQuery({
    queryKey: ["ind-courses", context.selectedClass],
    queryFn: async () => {
      if (!context.selectedClass) return [];
      const { data } = await supabase
        .from("courses")
        .select("id")
        .eq("class", context.selectedClass)
        .limit(1);
      return data ?? [];
    },
    enabled: !!context.selectedClass,
  });

  const updateModuleGrade = (moduleId: string, field: "grade" | "marks", value: string) => {
    setModuleGrades((prev) => ({
      ...prev,
      [moduleId]: { ...prev[moduleId], [field]: value, ...(prev[moduleId] ? {} : { grade: "", marks: "" }) },
    }));
  };

  const filledModules = modules.filter((m) => moduleGrades[m.id]?.grade?.trim());

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      if (!selectedStudentKey) throw new Error("Select a student");
      if (filledModules.length === 0) throw new Error("Enter at least one module grade");

      const courseId = courses[0]?.id;
      if (!courseId) throw new Error(`No course found for class ${context.selectedClass}`);

      const selected = studentOptions.find((s) => s.key === selectedStudentKey);
      if (!selected) throw new Error("Student not found");

      // Create or reuse batch (same logic as bulk upload)
      const { data: existingBatch } = await supabase
        .from("exam_batches")
        .select("id, status")
        .eq("class", context.selectedClass)
        .eq("semester", context.semester)
        .eq("exam_type", context.examType)
        .eq("academic_year", context.academicYear)
        .in("status", ["draft", "pending_approval", "approved"])
        .limit(1);

      let batchId: string;

      if (existingBatch && existingBatch.length > 0) {
        const existing = existingBatch[0];
        if (existing.status !== "draft") {
          throw new Error("A batch for this context is pending approval or approved. Resolve it first.");
        }
        batchId = existing.id;
      } else {
        const { data: batch, error: batchErr } = await supabase
          .from("exam_batches")
          .insert({
            class: context.selectedClass,
            semester: context.semester,
            exam_type: context.examType,
            academic_year: context.academicYear,
            uploaded_by: user.id,
            total_students: 1,
            total_errors: 0,
            status: "draft",
          })
          .select("id")
          .single();
        if (batchErr) throw batchErr;
        batchId = batch.id;
      }

      // Build result rows — one per filled module
      const overallUpper = overallRemark.toUpperCase();
      const isDiscontinueOrRepeat = overallUpper.includes("DISCONTINUE") || overallUpper.includes("REPEAT");

      // Determine which modules are "retake" — for individual entry, modules with grade indicating failure
      // But to match bulk format, we use overall remark logic
      const results = filledModules.map((m) => {
        const entry = moduleGrades[m.id];
        const gradeVal = entry.grade.trim();
        const marksVal = entry.marks ? Number(entry.marks) : null;

        let remark: string | null = null;
        if (overallRemark) {
          // Use grade-based derivation for individual modules
          const derived = deriveRemark(gradeVal);
          if (derived === "SUPP" && isDiscontinueOrRepeat) {
            remark = "FAIL";
          } else {
            remark = derived || overallRemark;
          }
        } else {
          remark = deriveRemark(gradeVal);
        }

        return {
          student_id: selected.studentId || null,
          pre_registration_id: selected.preRegId || null,
          student_source: isPre ? "pre_registered" : "signed_up",
          form_iv_index: selected.formIvIndex || null,
          course_id: courseId,
          marks: marksVal,
          grade: gradeVal || null,
          semester: context.semester,
          academic_year: context.academicYear,
          exam_type: `${context.examType} - ${m.module_code}`,
          batch_id: batchId,
          status: "pending" as const,
          remark,
          overall_remark: overallRemark || null,
          gpa: gpa || null,
        };
      });

      const { error: resErr } = await supabase.from("exam_results").insert(results);
      if (resErr) throw resErr;

      return { imported: results.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["exam-results"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      toast({ title: "Results saved", description: `${data.imported} module results saved as draft` });
      setSelectedStudentKey("");
      setModuleGrades({});
      setOverallRemark("");
      setGpa("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserPlus className="h-4 w-4 text-primary" />
          Individual Entry
        </CardTitle>
        <CardDescription>
          Enter results for a single student across all modules using {isPre ? "pre-registered" : "signed-up"} records
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Student selector */}
        <div className="max-w-md space-y-1.5">
          <Label className="text-xs">Student *</Label>
          <Select value={selectedStudentKey} onValueChange={setSelectedStudentKey}>
            <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
            <SelectContent>
              {studentOptions.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Module grades table */}
        {selectedStudentKey && modules.length > 0 && (
          <div className="border rounded-md overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Module Code</TableHead>
                  <TableHead className="text-xs">Module Name</TableHead>
                  <TableHead className="text-xs w-24">Grade *</TableHead>
                  <TableHead className="text-xs w-28">Marks (optional)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modules.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-xs font-mono">{m.module_code}</TableCell>
                    <TableCell className="text-xs">{m.module_name}</TableCell>
                    <TableCell>
                      <Input
                        className="h-8 text-xs"
                        value={moduleGrades[m.id]?.grade || ""}
                        onChange={(e) => updateModuleGrade(m.id, "grade", e.target.value)}
                        placeholder="e.g. B+"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        className="h-8 text-xs"
                        type="number"
                        min={0}
                        max={100}
                        value={moduleGrades[m.id]?.marks || ""}
                        onChange={(e) => updateModuleGrade(m.id, "marks", e.target.value)}
                        placeholder="0-100"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {selectedStudentKey && modules.length === 0 && (
          <p className="text-xs text-muted-foreground">No modules found for this class and semester. Add modules in the Modules tab first.</p>
        )}

        {/* Overall remark & GPA */}
        {selectedStudentKey && modules.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            <div className="space-y-1.5">
              <Label className="text-xs">Overall Remark</Label>
              <Input
                value={overallRemark}
                onChange={(e) => setOverallRemark(e.target.value)}
                placeholder="e.g. PASS, SUPP, DISCONTINUE"
                className="text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">GPA</Label>
              <Input
                value={gpa}
                onChange={(e) => setGpa(e.target.value)}
                placeholder="e.g. 3.2"
                className="text-xs"
              />
            </div>
          </div>
        )}

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!isReady || !selectedStudentKey || filledModules.length === 0 || saveMutation.isPending}
        >
          {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save {filledModules.length > 0 ? `${filledModules.length} Result${filledModules.length > 1 ? "s" : ""}` : "Results"}
        </Button>
      </CardContent>
    </Card>
  );
}
